/**
 * ComplaintBulkImportPage — vendor-aware CSV importer for complaint history.
 * CLARID-SPEC-MIG-003.
 *
 * Single page handles both steps: upload + preview-and-confirm. No modal; the
 * page itself is the workflow.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  ArrowUpTrayIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { complaintApi } from '../lib/api';
import { useTeamId } from '../hooks/useTeamId';

const VENDORS = [
  { value: 'NCONTRACTS', label: 'Ncontracts' },
  { value: 'NCINO', label: 'nCino' },
  { value: 'WOLTERS', label: 'Wolters Kluwer' },
  { value: 'CROWE', label: 'Crowe' },
  { value: 'COMPLYPILOT', label: 'ComplyPilot' },
  { value: 'GENERIC', label: 'Generic (auto-map)' },
] as const;

interface MappedRow {
  rowIndex: number;
  fields: Record<string, unknown>;
  warnings: string[];
  errors: string[];
}

interface Preview {
  detectedVendor: string;
  detectedConfidence: number;
  selectedVendor: string;
  headers: string[];
  mappedRows: MappedRow[];
  skippedRowIndices: number[];
  totalRows: number;
  validRowCount: number;
  errorRowCount: number;
  warningRowCount: number;
}

export default function ComplaintBulkImportPage() {
  const { teamId } = useTeamId();
  const navigate = useNavigate();
  const [csvText, setCsvText] = useState('');
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [enqueueAnalysis, setEnqueueAnalysis] = useState(true);

  const previewMutation = useMutation({
    mutationFn: async () => complaintApi.bulkImport.preview(teamId!, csvText),
    onSuccess: (res) => {
      setPreviewId(res.data.previewId);
      setPreview(res.data.preview);
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Preview failed'),
  });

  const updateMutation = useMutation({
    mutationFn: async (data: any) => complaintApi.bulkImport.updatePreview(teamId!, previewId!, data),
    onSuccess: (res) => setPreview(res.data.preview),
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Update failed'),
  });

  const commitMutation = useMutation({
    mutationFn: async () => complaintApi.bulkImport.commit(teamId!, previewId!, {
      enqueueAnalysis,
    }),
    onSuccess: (res) => {
      toast.success(`${res.data.committedCount} complaints imported${res.data.analysisEnqueued ? '. Batch analysis running.' : '.'}`);
      navigate('/complaints');
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Import failed'),
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setCsvText(String(reader.result || ''));
    reader.readAsText(file);
  };

  const skipRow = (rowIndex: number) => {
    if (!preview) return;
    const newSkip = preview.skippedRowIndices.includes(rowIndex)
      ? preview.skippedRowIndices.filter(i => i !== rowIndex)
      : [...preview.skippedRowIndices, rowIndex];
    updateMutation.mutate({ skipRows: newSkip });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <button
        onClick={() => navigate('/complaints')}
        className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
      >
        <ArrowLeftIcon className="h-4 w-4 mr-1" />
        Back to Complaints
      </button>

      <h1 className="text-2xl font-bold text-navy-900">Bulk import complaints</h1>
      <p className="mt-1 text-sm text-gray-500">
        Import historical complaint records from a competitor compliance vendor (Ncontracts, nCino,
        Wolters, Crowe, ComplyPilot). The system auto-detects the vendor from the CSV headers and
        maps each column to Clarid's complaint schema.
      </p>

      {/* Step 1: upload */}
      {!preview && (
        <div className="mt-6 bg-white rounded-lg shadow p-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Upload CSV file
          </label>
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={handleFileChange}
            className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-ocean-50 file:text-ocean-700 hover:file:bg-ocean-100"
          />
          <p className="mt-2 text-xs text-gray-500">Or paste CSV content below.</p>
          <textarea
            value={csvText}
            onChange={(e) => setCsvText(e.target.value)}
            rows={8}
            placeholder="reference,customer name,date,description,..."
            className="mt-3 block w-full font-mono text-xs border border-gray-300 rounded-md p-3 focus:border-ocean-500 focus:outline-none"
          />
          <button
            onClick={() => previewMutation.mutate()}
            disabled={!csvText.trim() || previewMutation.isPending}
            className="mt-4 inline-flex items-center px-4 py-2 bg-navy-900 text-white text-sm font-medium rounded-md hover:bg-navy-800 disabled:opacity-50"
          >
            <ArrowUpTrayIcon className="h-4 w-4 mr-2" />
            {previewMutation.isPending ? 'Detecting…' : 'Detect & preview'}
          </button>
        </div>
      )}

      {/* Step 2: preview & confirm */}
      {preview && (
        <div className="mt-6 space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Detected vendor</h2>
                <div className="mt-1 flex items-center gap-3">
                  <span className="text-xl font-bold text-navy-900">
                    {VENDORS.find(v => v.value === preview.selectedVendor)?.label}
                  </span>
                  <span className="text-sm text-gray-500">
                    {(preview.detectedConfidence * 100).toFixed(0)}% confidence
                  </span>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Override vendor</label>
                <select
                  value={preview.selectedVendor}
                  onChange={(e) => updateMutation.mutate({ vendorOverride: e.target.value })}
                  className="text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:border-ocean-500 focus:outline-none"
                >
                  {VENDORS.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
                </select>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
              <Stat label="Total rows" value={preview.totalRows} />
              <Stat label="Valid" value={preview.validRowCount} tone="good" />
              <Stat label="Errors" value={preview.errorRowCount} tone={preview.errorRowCount > 0 ? 'bad' : 'neutral'} />
              <Stat label="Warnings" value={preview.warningRowCount} tone={preview.warningRowCount > 0 ? 'warn' : 'neutral'} />
            </div>
          </div>

          {/* Row preview table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-base font-semibold text-navy-900">Row preview</h3>
              <p className="text-xs text-gray-500 mt-0.5">Showing first 50 rows. Toggle any row to skip it from the commit.</p>
            </div>
            <div className="overflow-x-auto max-h-[500px]">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left font-semibold text-gray-500 uppercase tracking-wider">#</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-500 uppercase tracking-wider">Customer</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-500 uppercase tracking-wider">Category</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-500 uppercase tracking-wider">Description</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-500 uppercase tracking-wider">Issues</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-500 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {preview.mappedRows.slice(0, 50).map(row => {
                    const skipped = preview.skippedRowIndices.includes(row.rowIndex);
                    const hasError = row.errors.length > 0;
                    return (
                      <tr key={row.rowIndex} className={skipped ? 'bg-gray-50 opacity-50' : hasError ? 'bg-red-50' : ''}>
                        <td className="px-4 py-2 text-gray-500">{row.rowIndex + 1}</td>
                        <td className="px-4 py-2 font-medium">{String(row.fields.customerName ?? '—')}</td>
                        <td className="px-4 py-2">{String(row.fields.category ?? '—')}</td>
                        <td className="px-4 py-2">{String(row.fields.status ?? '—')}</td>
                        <td className="px-4 py-2 text-gray-600 max-w-md truncate">{String(row.fields.description ?? '').slice(0, 100)}</td>
                        <td className="px-4 py-2">
                          {row.errors.length > 0 && (
                            <span className="inline-flex items-center text-red-600" title={row.errors.join('; ')}>
                              <XCircleIcon className="h-4 w-4 mr-1" />
                              {row.errors.length}
                            </span>
                          )}
                          {row.errors.length === 0 && row.warnings.length > 0 && (
                            <span className="inline-flex items-center text-amber-600" title={row.warnings.join('; ')}>
                              <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                              {row.warnings.length}
                            </span>
                          )}
                          {row.errors.length === 0 && row.warnings.length === 0 && (
                            <CheckCircleIcon className="h-4 w-4 text-green-600" />
                          )}
                        </td>
                        <td className="px-4 py-2">
                          <button
                            onClick={() => skipRow(row.rowIndex)}
                            className="text-xs text-ocean-600 hover:text-ocean-800"
                          >
                            {skipped ? 'Include' : 'Skip'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Commit footer */}
          <div className="bg-white rounded-lg shadow p-6">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={enqueueAnalysis}
                onChange={(e) => setEnqueueAnalysis(e.target.checked)}
                className="rounded border-gray-300 text-ocean-600 focus:ring-ocean-500"
              />
              Run batch analysis after import (recommended — surfaces themes and risk patterns)
            </label>
            <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
              <p className="text-sm text-gray-500">
                Importing <strong className="text-navy-900">{preview.validRowCount}</strong> of {preview.totalRows} rows.
                {preview.errorRowCount > 0 && ` ${preview.errorRowCount} rows have errors and will be skipped.`}
              </p>
              <button
                onClick={() => commitMutation.mutate()}
                disabled={preview.validRowCount === 0 || commitMutation.isPending}
                className="inline-flex items-center px-6 py-2.5 bg-navy-900 text-white text-sm font-semibold rounded-md hover:bg-navy-800 disabled:opacity-50"
              >
                {commitMutation.isPending ? 'Importing…' : `Import ${preview.validRowCount} complaints`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone = 'neutral' }: { label: string; value: number; tone?: 'good' | 'warn' | 'bad' | 'neutral' }) {
  const color = tone === 'good' ? 'text-green-600' : tone === 'warn' ? 'text-amber-600' : tone === 'bad' ? 'text-red-600' : 'text-navy-900';
  return (
    <div>
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{label}</div>
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
    </div>
  );
}
